import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const TOOLBAR = [
  [{ header: [2, 3, 4, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['link'],
  ['clean'],
];

/**
 * Thin React 19-compatible Quill wrapper.
 * Props: value (HTML string), onChange (html => void), placeholder, style, error (bool), maxWords, onLimitExceeded
 */
export default function QuillEditor({ value, onChange, placeholder = '', style, error, maxWords, onLimitExceeded }) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onLimitExceededRef = useRef(onLimitExceeded);
  const maxWordsRef = useRef(maxWords);

  const trimToMaxWords = (quill, limit) => {
    let wordsSeen = 0;
    let trimIndex = null;
    const text = quill.getText();
    const matches = text.matchAll(/\S+/g);

    for (const match of matches) {
      wordsSeen += 1;
      if (wordsSeen > limit) {
        trimIndex = match.index;
        break;
      }
    }

    if (trimIndex === null) return false;

    quill.deleteText(trimIndex, quill.getLength() - trimIndex, 'silent');
    quill.setSelection(quill.getLength() - 1, 0, 'silent');
    return true;
  };

  // Keep callback ref current without re-initialising Quill
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onLimitExceededRef.current = onLimitExceeded; }, [onLimitExceeded]);
  useEffect(() => { maxWordsRef.current = maxWords; }, [maxWords]);

  // Mount Quill once
  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    const q = new Quill(containerRef.current, {
      theme: 'snow',
      placeholder,
      modules: { toolbar: TOOLBAR },
    });

    // Set initial HTML
    if (value) {
      q.clipboard.dangerouslyPasteHTML(value);
    }

    q.on('text-change', () => {
      const limit = maxWordsRef.current;
      const editor = containerRef.current.querySelector('.ql-editor');
      const text = q.getText().trim();
      const wordCount = text ? text.split(/\s+/).length : 0;

      if (limit && wordCount > limit && trimToMaxWords(q, limit)) {
        onLimitExceededRef.current?.();
      }

      const html = editor.innerHTML;
      onChangeRef.current(html === '<p><br></p>' ? '' : html);
    });

    quillRef.current = q;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync value from outside only when it differs from editor content
  useEffect(() => {
    if (!quillRef.current) return;
    const editorHtml = containerRef.current?.querySelector('.ql-editor')?.innerHTML ?? '';
    const incoming = value ?? '';
    if (incoming !== editorHtml && incoming !== (editorHtml === '<p><br></p>' ? '' : editorHtml)) {
      quillRef.current.clipboard.dangerouslyPasteHTML(incoming);
    }
  }, [value]);

  return (
    <div
      style={{
        border: error ? '1px solid #dc3545' : '1px solid #ced4da',
        borderRadius: 4,
        ...style,
      }}
    >
      <style>{`.ql-editor { height: 14rem; overflow-y: auto; }`}</style>
      <div ref={containerRef} />
    </div>
  );
}
